import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Calendar, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function DailyCollection() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [paying, setPaying] = useState(false);
  const [togglingAP, setTogglingAP] = useState(false);

  const load = () => api.get("/customer/collections").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const payNow = async () => {
    setPaying(true);
    try {
      await api.post("/customer/collections/pay-now");
      toast.success("Today's collection paid!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setPaying(false); }
  };

  const toggleAutoPay = async () => {
    if (!d) return;
    setTogglingAP(true);
    try {
      await api.patch("/customer/autopay", { enabled: !d.autopay_enabled });
      toast.success(`AutoPay turned ${!d.autopay_enabled ? "ON" : "OFF"}`);
      load();
    } catch (e) {
      toast.error("Failed");
    } finally { setTogglingAP(false); }
  };

  if (!d) {
    return <div className="p-6 text-center text-slate-500" data-testid="daily-collection-loading">Loading...</div>;
  }

  const todayDone = d.today_collected >= d.daily_target && d.daily_target > 0;
  const todayPercent = d.daily_target > 0 ? Math.min(100, (d.today_collected / d.daily_target) * 100) : 0;
  const monthPercent = d.monthly_target > 0 ? Math.min(100, (d.month_collected / d.monthly_target) * 100) : 0;
  const visibleHistory = showAll ? d.history : d.history.slice(0, 4);

  return (
    <div data-testid="daily-collection-page" className="pb-24">
      {/* Green header */}
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-green-600 text-white px-5 pt-6 pb-6 rounded-b-[2rem]">
        <div className="flex items-center gap-3">
          <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="font-extrabold tracking-tight text-lg">Daily Collection</div>
        </div>
      </div>

      {/* Today's Collection Card */}
      <div className="px-5 -mt-4">
        <div className="bg-white border border-emerald-100 rounded-3xl p-5 card-shadow-lg relative overflow-hidden">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="bg-emerald-600 text-white rounded-2xl px-4 py-3.5 inline-block">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-90 font-bold">Today's Collection</div>
                <div data-testid="today-collected-amount" className="text-3xl font-extrabold tracking-tight mt-1">{fmtINR(d.today_collected)}</div>
                <div className="text-[10px] opacity-80 mt-0.5">Target: {fmtINR(d.daily_target)}</div>
              </div>
            </div>
            <div className="shrink-0 relative">
              <CircularProgress percent={todayPercent} done={todayDone} />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            {todayDone ? (
              <span data-testid="badge-completed" className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                Completed <CheckCircle2 className="w-3.5 h-3.5" />
              </span>
            ) : (
              <span data-testid="badge-due" className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                Pending
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Target Card */}
      <div className="px-5 mt-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 card-shadow" data-testid="monthly-target-card">
          <div className="flex items-center justify-between">
            <div className="font-extrabold tracking-tight text-slate-800 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600" /> Monthly Target
            </div>
            <span data-testid="monthly-percent" className="font-extrabold text-emerald-600">{Math.round(monthPercent)}%</span>
          </div>
          <div className="mt-2.5 text-lg font-extrabold tracking-tight">
            <span className="text-emerald-600">{fmtINR(d.month_collected)}</span>
            <span className="text-slate-400 font-bold"> / {fmtINR(d.monthly_target)}</span>
          </div>
          <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-green-600 transition-all" style={{ width: `${monthPercent}%` }} />
          </div>
          <div className="text-[11px] text-slate-400 mt-2">For {d.days_in_month} days · {fmtINR(d.daily_target)}/day</div>
        </div>
      </div>

      {/* Pay Now button (full width since AutoPay lives on Saving Wallet) */}
      <div className="px-5 mt-4">
        <button
          data-testid="pay-now-btn"
          onClick={payNow}
          disabled={paying || todayDone}
          className={`w-full rounded-2xl py-4 font-extrabold text-base tap-scale shadow-md flex items-center justify-center gap-2 ${
            todayDone ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-tenant-primary text-white hover:opacity-95"
          }`}
        >
          {paying ? "Processing..." : todayDone ? "Paid ✓" : "Pay Now (Add to Saving Wallet)"}
        </button>
        {d.autopay_enabled && (
          <div data-testid="autopay-on-info" className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 inline-flex items-center gap-1.5">
            <Zap className="w-3 h-3" /> AutoPay is ON · Premium will be auto-paid from Saving Wallet
          </div>
        )}
      </div>

      {/* Collection History */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-extrabold tracking-tight">Collection History</h3>
          <span className="text-[11px] text-slate-400 font-bold">{d.history.length} entries</span>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl card-shadow divide-y divide-slate-100">
          {visibleHistory.map((c) => (
            <div key={c.id} data-testid={`history-${c.id}`} className="px-4 py-3 flex items-center justify-between">
              <div>
                <div className="font-bold text-sm">{fmtDate(c.created_at)}</div>
                <div className="text-[11px] text-slate-400 capitalize">via {c.method}</div>
              </div>
              <div className="font-extrabold text-emerald-600">{fmtINR(c.amount)}</div>
            </div>
          ))}
          {d.history.length === 0 && <div className="text-center text-slate-400 text-sm py-8">No collections yet.</div>}
        </div>
        {d.history.length > 4 && (
          <button data-testid="toggle-view-all" onClick={() => setShowAll(!showAll)} className="w-full text-center text-sm font-bold text-tenant-primary py-3">
            {showAll ? "Show less" : `View All (${d.history.length})`}
          </button>
        )}
      </div>
    </div>
  );
}

const CircularProgress = ({ percent = 0, done = false }) => {
  const radius = 36;
  const circ = 2 * Math.PI * radius;
  const dash = (percent / 100) * circ;
  return (
    <div className="relative w-[88px] h-[88px]">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={radius} stroke="#D1FAE5" strokeWidth="6" fill="none" />
        <circle
          cx="40" cy="40" r={radius}
          stroke={done ? "#059669" : "#10B981"}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        {done ? (
          <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={2.5} />
        ) : (
          <div className="text-emerald-700 font-extrabold text-sm">{Math.round(percent)}%</div>
        )}
      </div>
    </div>
  );
};
